import { ChangeDetectionStrategy, Component, computed, inject, Injector } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { LOCALE_CONFIG } from '@locale/locale-config.provider';
import { DRESSFIT_LOGO, DressfitBridge } from '@pixacare/dressfit-angular';
import { TuiButton, TuiDialogService } from '@taiga-ui/core';
import { TuiFade } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { formatNameList } from '../../utils/format-name-list';
import { formatPriceCents } from '../../utils/format-price-cents';
import { DressfitPanelDialogComponent } from '../dressfit-panel-dialog/dressfit-panel-dialog.component';

// Recommended families fit two to a line under their count; a kept dressing's name is set larger.
const MAX_SHOWN_FAMILY_NAMES = 2;
const MAX_SHOWN_DRESSING_NAMES = 1;

/**
 * The DressFit panel folded into one line, for wherever there is no room for the column: where the
 * session stands (`DressfitBridge.summary`, built from what the panel shows, so the two never
 * disagree), and "Details" to open the panel itself in a dialog, in the same session.
 */
@Component({
  selector: 'pxc-dressfit-banner',
  templateUrl: './dressfit-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, TuiButton, TuiFade],
  host: {
    class: 'flex min-h-16 w-full items-center gap-3 overflow-hidden px-4 py-3',
  },
})
export class DressfitBannerComponent {
  private readonly bridge = inject(DressfitBridge);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly translocoService = inject(TranslocoService);
  private readonly localeCode = inject(LOCALE_CONFIG).code;

  protected readonly summary = this.bridge.summary;
  protected readonly logo = DRESSFIT_LOGO;

  protected readonly priceRange = computed(() => {
    const summary = this.summary();
    const range = summary.status === 'recommendations' ? summary.priceRange : undefined;
    if (!range) {
      return undefined;
    }
    const minPrice = formatPriceCents(range.minCents, this.localeCode);
    // A single size states a price, not a range.
    return range.minCents === range.maxCents
      ? minPrice
      : `${minPrice} – ${formatPriceCents(range.maxCents, this.localeCode)}`;
  });

  protected readonly familyNames = computed(() => {
    const summary = this.summary();
    return summary.status === 'recommendations' ? this.formatNames(summary.familyNames, MAX_SHOWN_FAMILY_NAMES) : '';
  });

  protected readonly dressingNames = computed(() => {
    const summary = this.summary();
    return summary.status === 'selected' ? this.formatNames(summary.dressingNames, MAX_SHOWN_DRESSING_NAMES) : '';
  });

  openPanel(): void {
    // The component's own injector, so the dialog reads the same DressFit session. Borderless and
    // without a label: the panel brings its own header and fills the dialog edge to edge.
    this.dialogService
      .open<void>(new PolymorpheusComponent(DressfitPanelDialogComponent, this.injector), {
        size: 's',
        appearance: 'taiga borderless',
      })
      .subscribe();
  }

  private formatNames(names: readonly string[], maxShown: number): string {
    return formatNameList(
      names,
      maxShown,
      (count) => this.translocoService.translate('{count, plural, one {# autre} other {# autres}}', { count }),
      this.localeCode,
    );
  }
}
